"use client";

import { useEffect } from "react";
import {
  dispatchAppUpdateRequired,
  isStaleBuildError,
} from "@/lib/app-update";

// ─── Client Error Boundary ───
// Captures unhandled errors outside React's render tree
// (event handlers, async operations, third-party scripts)

export function ClientErrorBoundary({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    function handlePotentialStaleBuild(error: unknown) {
      if (isStaleBuildError(error)) {
        dispatchAppUpdateRequired("stale-build-error");
      }
    }

    function reportError(payload: {
      message: string;
      stack?: string;
      url?: string;
    }) {
      fetch("/api/error-report", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...payload,
          userAgent: navigator.userAgent,
          timestamp: new Date().toISOString(),
        }),
      }).catch(() => {});
    }

    function handleError(event: ErrorEvent) {
      handlePotentialStaleBuild(event.error ?? event.message);
      reportError({
        message: event.message || "Unhandled error",
        stack: event.error?.stack,
        url: window.location.href,
      });
    }

    function handleUnhandledRejection(event: PromiseRejectionEvent) {
      const reason = event.reason;
      const message =
        reason instanceof Error
          ? reason.message
          : String(reason || "Unhandled promise rejection");

      handlePotentialStaleBuild(reason);
      reportError({
        message,
        stack: reason instanceof Error ? reason.stack : undefined,
        url: window.location.href,
      });
    }

    window.addEventListener("error", handleError);
    window.addEventListener("unhandledrejection", handleUnhandledRejection);

    return () => {
      window.removeEventListener("error", handleError);
      window.removeEventListener("unhandledrejection", handleUnhandledRejection);
    };
  }, []);

  return <>{children}</>;
}
